import type { ReactNode } from "react";
import { useState } from "react";
import { navigate } from "@reach/router";
import { useTranslation } from "react-i18next";
import { useFirebaseGame } from "src/hooks/useFirebaseGame";
import { useAuth } from "src/contexts/AuthContext";
import type { Theme } from "src/theme";

type Props = {
  path: string;
};

const containerStyle = ({ colors, gridUnit }: Theme) => ({
  position: "fixed" as const,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flexDirection: "column" as const,
  background: colors.grey95,
  overflowY: "auto" as const,
  "@media (min-width: 480px)": {
    justifyContent: "center"
  }
});

const cardStyle = ({ colors, gridUnit }: Theme) => ({
  background: "white",
  color: colors.textPrimary,
  padding: `${gridUnit * 6}px ${gridUnit * 5}px`,
  width: "100%",
  minHeight: "100vh",
  boxSizing: "border-box" as const,
  paddingTop: gridUnit * 18,
  "@media (min-width: 480px)": {
    padding: gridUnit * 10,
    paddingTop: gridUnit * 10,
    borderRadius: gridUnit * 3,
    boxShadow: `0 ${gridUnit}px ${gridUnit * 1.5}px rgba(0,0,0,0.1)`,
    minWidth: "400px",
    maxWidth: "500px",
    minHeight: "auto",
    width: "auto"
  }
});

const inputStyle =
  (error: boolean) =>
  ({ colors, gridUnit }: Theme) => ({
    width: "100%",
    boxSizing: "border-box" as const,
    padding: `${gridUnit * 3}px ${gridUnit * 4}px`,
    fontSize: gridUnit * 4.5,
    border: error ? `2px solid #f44336` : `2px solid ${colors.grey8}`,
    borderRadius: gridUnit * 1.5,
    outline: "none",
    textAlign: "center" as const,
    letterSpacing: "4px",
    fontWeight: "bold",
    textTransform: "uppercase" as const,
    transition: "border-color 0.2s",
    "&:focus": {
      borderColor: colors.primary
    },
    "&:disabled": {
      background: colors.grey95,
      cursor: "not-allowed"
    }
  });

const errorMessageStyle = ({ colors, gridUnit }: Theme) => ({
  marginTop: gridUnit * 2,
  color: "#f44336",
  fontSize: gridUnit * 3.5,
  textAlign: "center" as const
});

const infoBoxStyle = ({ colors, gridUnit }: Theme) => ({
  marginTop: gridUnit * 6,
  padding: gridUnit * 4,
  background: colors.grey95,
  borderRadius: gridUnit * 1.5,
  fontSize: gridUnit * 3.5,
  color: colors.grey1
});

const JoinGame = (props: Props): ReactNode => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [roomCode, setRoomCode] = useState<string>("");
  const [joining, setJoining] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { joinGame } = useFirebaseGame(null);

  // Wait for authentication
  if (!user) {
    return (
      <div css={containerStyle}>
        <div css={(theme) => ({ color: theme.colors.textPrimary })}>{t("joinGame.authenticating")}</div>
      </div>
    );
  }

  const handleJoinGame = async () => {
    if (!user) return;

    if (!roomCode || roomCode.length !== 6) {
      setError(t("joinGame.errorInvalid"));
      return;
    }

    setJoining(true);
    setError(null);

    const game = await joinGame(roomCode.toUpperCase(), user.uid);

    if (game) {
      navigate(`/multiplayer/game/${game.gameId}`);
    } else {
      setError(t("joinGame.errorFailed"));
      setJoining(false);
    }
  };

  const handleRoomCodeChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 6) {
      setRoomCode(value);
      setError(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !joining) {
      handleJoinGame();
    }
  };

  return (
    <div css={containerStyle}>
      <div css={cardStyle}>
        <h1
          css={(theme) => ({
            marginTop: 0,
            marginBottom: theme.gridUnit * 5,
            textAlign: "center",
            fontSize: theme.gridUnit * 6,
            "@media (min-width: 480px)": {
              marginBottom: theme.gridUnit * 7.5,
              fontSize: "2em"
            }
          })}
        >
          {t("joinGame.title")}
        </h1>

        <div css={(theme) => ({ marginBottom: theme.gridUnit * 6 })}>
          <label
            htmlFor="roomCode"
            css={(theme) => ({ display: "block", marginBottom: theme.gridUnit * 2, fontWeight: "bold" })}
          >
            {t("joinGame.roomCode")}
          </label>
          <input
            id="roomCode"
            type="text"
            value={roomCode}
            onChange={handleRoomCodeChange}
            onKeyPress={handleKeyPress}
            placeholder={t("joinGame.placeholder")}
            disabled={joining}
            css={inputStyle(!!error)}
          />
          {error && <div css={errorMessageStyle}>{error}</div>}
        </div>

        <div
          css={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.gridUnit * 3,
            "@media (min-width: 480px)": {
              flexDirection: "row"
            }
          })}
        >
          <button
            css={(theme) => ({
              ...theme.primaryButton,
              flex: 1,
              padding: theme.gridUnit * 4,
              fontSize: theme.gridUnit * 4,
              "@media (min-width: 480px)": {
                padding: theme.gridUnit * 3
              }
            })}
            onClick={handleJoinGame}
            disabled={joining || !roomCode}
          >
            {joining ? t("joinGame.joining") : t("joinGame.join")}
          </button>
          <button
            css={(theme) => ({
              ...theme.secondaryButton,
              flex: 1,
              padding: theme.gridUnit * 4,
              fontSize: theme.gridUnit * 4,
              "@media (min-width: 480px)": {
                padding: theme.gridUnit * 3
              }
            })}
            onClick={() => navigate("/multiplayer")}
            disabled={joining}
          >
            {t("common.cancel")}
          </button>
        </div>

        <div css={infoBoxStyle}>
          <p css={(theme) => ({ margin: 0, marginBottom: theme.gridUnit * 2 })}>
            <strong>{t("joinGame.howToJoin")}</strong>
          </p>
          <ol css={(theme) => ({ margin: 0, paddingLeft: theme.gridUnit * 5 })}>
            <li>{t("joinGame.step1")}</li>
            <li>{t("joinGame.step2")}</li>
            <li>{t("joinGame.step3")}</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default JoinGame;
